import {controller, target} from '@github/catalyst'
import {fetchPoll} from '@github-ui/fetch-utils'
import {isShortcutAllowed} from '@github-ui/hotkey/keyboard-shortcuts-helper'
import {offset} from '@primer/behaviors'
import {throttle} from '@github/mini-throttle'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'

function getCssVar(property: string) {
  return getComputedStyle(document.documentElement).getPropertyValue(property)
}

type UserMap = {[key: string]: User}

type SpaceMap = Array<Array<[number, number]>>

interface UserBlock {
  count: number
  name: string
  start: number
}

interface Meta {
  users: User[]
  dates: string
  blocks: UserBlock[]
  focus: number
  nethash: string
  spacemap: SpaceMap
}

interface Chunk {
  commits?: CommitData[]
}

class Network {
  container: HTMLElement
  width: number
  height: number
  minimalHeight: number
  ctx: CanvasRenderingContext2D

  focus: number | null = null
  nethash?: string
  spaceMap?: SpaceMap
  userBlocks?: UserBlock[]
  commits?: unknown
  users?: UserMap

  chrome?: NetworkChrome
  graph?: NetworkGraph
  mouseDriver?: MouseDriver
  keyDriver?: KeyDriver

  constructor(container: HTMLElement) {
    this.container = container
    this.minimalHeight = 480
    this.width = 0
    this.height = this.minimalHeight
    this.ctx = this.initCanvas(this.container)
    // eslint-disable-next-line i18n-text/no-en
    this.startLoader('Loading graph data')
    this.loadMeta()
  }

  redraw() {
    this.ctx = this.initCanvas(this.container)
    if (this.graph != null) {
      this.graph.draw()
    }
    if (this.chrome != null) {
      this.chrome.draw()
    }
  }

  initCanvas(container: HTMLElement): CanvasRenderingContext2D {
    const canvas = container.getElementsByTagName('canvas')[0]!

    this.width = container.offsetWidth
    this.height = Math.max(this.minimalHeight, this.height + window.innerHeight - document.body.scrollHeight)

    canvas.style.zIndex = '0'
    const context = canvas.getContext('2d')!
    const devicePixelRatio = window.devicePixelRatio || 1
    const ratio = devicePixelRatio
    canvas.width = this.width * ratio
    canvas.height = this.height * ratio
    canvas.style.width = `${this.width}px`
    canvas.style.height = `${this.height}px`
    context.scale(ratio, ratio)
    return context
  }

  startLoader(text: string) {
    this.ctx.save()
    this.ctx.font = "14px 'Helvetica Neue', Arial, sans-serif"
    this.ctx.fillStyle = '#cacaca'
    this.ctx.textAlign = 'center'
    this.ctx.fillText(text, this.width / 2, 155)
    this.ctx.restore()
    this.displayLoader()
  }

  get loader() {
    return this.container.querySelector<HTMLElement>('.js-network-graph-loading-area')!
  }

  stopLoader() {
    this.loader.hidden = true
  }

  displayLoader() {
    this.loader.hidden = false
  }

  async loadMeta() {
    try {
      const url = this.container.getAttribute('data-network-graph-meta-url')!
      const response = await fetchPoll(url, {
        headers: {
          accept: 'application/json',
        },
      })
      const data: Meta = await response.json()
      this.init(data)
    } catch {
      this.initError()
    }
  }

  init(data: Meta) {
    let date
    let i
    let len
    if (!viewingNetwork) {
      return
    }
    this.focus = data.focus
    const nethash = (this.nethash = data.nethash)
    const spaceMap = (this.spaceMap = data.spacemap)
    const userBlocks = (this.userBlocks = data.blocks)
    const commits = (this.commits = (function () {
      const ref1 = data.dates
      const results = []
      for (let index = 0; index < ref1.length; ++index) {
        date = ref1[index]!
        results.push(new Commit(index, date))
      }
      return results
    })())
    const users = (this.users = {})
    const ref1 = data.users
    for (i = 0, len = ref1.length; i < len; i++) {
      const user = ref1[i]!
      this.users[user.name] = user!
    }
    const chrome = (this.chrome = new NetworkChrome(this, this.ctx, commits, userBlocks, users))
    const graph = (this.graph = new NetworkGraph(this, this.ctx, commits, users, spaceMap, userBlocks, nethash))
    this.mouseDriver = new MouseDriver(this.container, chrome, graph)
    this.keyDriver = new KeyDriver(chrome, graph)
    this.stopLoader()
    graph.drawBackground()
    chrome.draw()
    graph.requestInitialChunk()
  }

  initError() {
    this.stopLoader()
    this.ctx.clearRect(0, 0, this.width, this.height)
    // eslint-disable-next-line i18n-text/no-en
    this.startLoader('Graph could not be drawn due to a network problem.')
  }
}

interface CommitData {
  time: number
  date: string
  author: string
  gravatar: string
  id: string
  login?: string
  message?: string
  space: number
  parents: Array<[string, number, number]>
}

class Commit {
  time: number
  date: Date
  requested: boolean
  populated: Date | null

  user: User | null = null
  author: string | null = null
  gravatar: string | null = null
  id: string | null = null
  login?: string
  message: string | null | undefined
  space: number
  parents: Commit[]

  constructor(time1: number, date: string) {
    this.time = time1
    this.date = new Date(date)
    this.requested = false
    this.populated = null
    this.message = null
    this.space = 0
    this.parents = []
  }

  populate(commit: CommitData, user: User, commits: Commit[]) {
    this.user = user
    this.author = commit.author
    this.date = new Date(commit.date.replace(' ', 'T'))
    this.gravatar = commit.gravatar
    this.id = commit.id
    this.login = commit.login
    this.message = commit.message
    this.space = commit.space
    this.time = commit.time
    this.parents = this.populateParents(commit.parents, commits)
    this.requested = true
    this.populated = new Date()
  }

  populateParents(parents: Array<[string, number, number]>, commits: Commit[]): Commit[] {
    return parents.map(([id, index, space]) => {
      const parentCommit = commits[index]!
      parentCommit.id = id
      parentCommit.space = space
      return parentCommit
    })
  }
}

interface User {
  name: string
  repo: string
  heads: Head[]
}

interface Head {
  id: string
  name: string
}

class NetworkChrome {
  network: Network
  ctx: CanvasRenderingContext2D
  commits: Commit[]
  userBlocks: UserBlock[]
  users: UserMap
  namesWidth: number
  months: string[]
  userBgColors: string[]
  headerColor: string
  dividerColor: string
  headerHeight: number
  dateRowHeight: number
  graphTopOffset: number
  nameLineHeight: number
  offsetX: number
  offsetY: number
  contentHeight: number
  graphMidpoint: number
  activeUser: User | null

  constructor(
    network: Network,
    ctx1: CanvasRenderingContext2D,
    commits1: Commit[],
    userBlocks: UserBlock[],
    users: UserMap,
  ) {
    this.network = network
    this.ctx = ctx1
    this.commits = commits1
    this.userBlocks = userBlocks
    this.users = users
    this.namesWidth = 120
    this.months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    this.userBgColors = [
      getCssVar('--color-canvas-default') || getCssVar('--bgColor-default'),
      getCssVar('--color-canvas-subtle') || getCssVar('--bgColor-muted'),
    ]
    this.headerColor = getCssVar('--color-canvas-subtle') || getCssVar('--bgColor-muted')
    this.dividerColor = getCssVar('--color-border-default') || getCssVar('--borderColor-default')
    this.headerHeight = 40
    this.dateRowHeight = 30
    this.graphTopOffset = 10 + this.headerHeight + this.dateRowHeight
    this.nameLineHeight = 24
    this.offsetX = this.namesWidth + (network.width - this.namesWidth) / 2 - network.focus! * this.nameLineHeight
    this.offsetY = 0
    this.contentHeight = this.calcContentHeight()
    this.graphMidpoint = this.namesWidth + (network.width - this.namesWidth) / 2
    this.activeUser = null
  }

  moveX(deltaX: number) {
    this.offsetX += deltaX
    if (this.offsetX > this.graphMidpoint) {
      this.offsetX = this.graphMidpoint
    } else if (this.offsetX < this.graphMidpoint - this.commits.length * this.nameLineHeight) {
      this.offsetX = this.graphMidpoint - this.commits.length * this.nameLineHeight
    }
  }

  moveY(deltaY: number) {
    this.offsetY += deltaY
    if (this.offsetY > 0 || this.contentHeight < this.network.height - this.graphTopOffset) {
      this.offsetY = 0
    } else if (this.offsetY < -this.contentHeight + this.network.height / 2) {
      this.offsetY = -this.contentHeight + this.network.height / 2
    }
  }

  calcContentHeight() {
    let i
    let len
    let height = 0
    const ref1 = this.userBlocks
    for (i = 0, len = ref1.length; i < len; i++) {
      const block = ref1[i]!
      height += block.count
    }
    return height * this.nameLineHeight
  }

  hover(x: number, y: number) {
    let i
    let len
    const ref1 = this.userBlocks
    for (i = 0, len = ref1.length; i < len; i++) {
      const block = ref1[i]!
      if (x > 0 && x < this.namesWidth) {
        if (y > this.graphTopOffset + this.offsetY + block.start * this.nameLineHeight) {
          if (y < this.graphTopOffset + this.offsetY + (block.start + block.count) * this.nameLineHeight) {
            return this.users[block.name]
          }
        }
      }
    }
    return null
  }

  draw() {
    this.drawTimeline(this.ctx)
    this.drawUsers(this.ctx)
  }

  drawTimeline(ctx: CanvasRenderingContext2D) {
    let i
    let ix
    let lastDay
    let lastMonth
    let maxCommitIndex
    let minCommitIndex
    let ref1
    let ref2
    ctx.fillStyle = this.headerColor
    ctx.fillRect(0, 0, this.network.width, this.headerHeight)
    ctx.fillStyle = this.dividerColor
    ctx.fillRect(0, this.headerHeight - 1, this.network.width, 1)
    minCommitIndex = Math.floor((0 - this.offsetX) / this.nameLineHeight)
    if (minCommitIndex < 0) {
      minCommitIndex = 0
    }
    maxCommitIndex = Math.floor(minCommitIndex + this.network.width / (this.nameLineHeight - 1))
    if (maxCommitIndex > this.commits.length) {
      maxCommitIndex = this.commits.length
    }
    ctx.save()
    ctx.translate(this.offsetX, 0)
    lastMonth = null
    lastDay = null
    for (
      ix = i = ref1 = minCommitIndex, ref2 = maxCommitIndex;
      ref1 <= ref2 ? i < ref2 : i > ref2;
      ix = ref1 <= ref2 ? ++i : --i
    ) {
      const commit = this.commits[ix]!
      const monthName = this.months[commit.date.getMonth()]!
      if (monthName !== lastMonth) {
        ctx.font = "bold 12px 'Helvetica Neue', Arial, sans-serif"
        ctx.fillStyle = getCssVar('--color-fg-default') || getCssVar('--fgColor-default')
        const monthWidth = this.ctx.measureText(monthName).width
        ctx.fillText(monthName, ix * this.nameLineHeight - monthWidth / 2, this.headerHeight / 2 + 4)
        lastMonth = monthName
      }
      const day = commit.date.getDate()
      if (day !== lastDay) {
        ctx.font = "12px 'Helvetica Neue', Arial, sans-serif"
        ctx.fillStyle = getCssVar('--color-fg-default') || getCssVar('--fgColor-default')
        const dateWidth = this.ctx.measureText(day.toString()).width
        ctx.fillText(
          day.toString(),
          ix * this.nameLineHeight - dateWidth / 2,
          this.headerHeight + this.dateRowHeight / 2 + 3,
        )
        lastDay = day
        ctx.fillStyle = getCssVar('--color-border-default') || getCssVar('--borderColor-default')
        ctx.fillRect(ix * this.nameLineHeight, this.headerHeight, 1, 6)
      }
    }
    ctx.restore()
  }

  drawUsers(ctx: CanvasRenderingContext2D) {
    let i
    let ix
    let len
    ctx.fillStyle = getCssVar('--color-canvas-default') || getCssVar('--bgColor-default')
    ctx.fillRect(0, 0, this.namesWidth, this.network.height)
    ctx.save()
    ctx.translate(0, this.headerHeight + this.dateRowHeight + this.offsetY)
    const ref1 = this.userBlocks
    for (ix = i = 0, len = ref1.length; i < len; ix = ++i) {
      const block = ref1[ix]!
      ctx.fillStyle = this.userBgColors[ix % 2]!
      ctx.fillRect(0, block.start * this.nameLineHeight, this.namesWidth, block.count * this.nameLineHeight)
      if (this.activeUser && this.activeUser.name === block.name) {
        ctx.fillStyle = getCssVar('--color-border-default') || getCssVar('--borderColor-default')
        ctx.fillRect(0, block.start * this.nameLineHeight, this.namesWidth, block.count * this.nameLineHeight)
      }
      const nameY = (block.start + block.count / 2) * this.nameLineHeight + 3
      ctx.fillStyle = getCssVar('--color-border-default') || getCssVar('--borderColor-default')
      ctx.fillRect(0, block.start * this.nameLineHeight + block.count * this.nameLineHeight - 1, this.namesWidth, 1)
      ctx.fillStyle = getCssVar('--color-fg-default') || getCssVar('--fgColor-default')
      ctx.font = "13px 'Helvetica Neue', Arial, sans-serif"
      ctx.textAlign = 'center'
      ctx.fillText(block.name, this.namesWidth / 2, nameY, 96)
    }
    ctx.restore()
    ctx.fillStyle = this.headerColor
    ctx.fillRect(0, 0, this.namesWidth, this.headerHeight)
    ctx.fillStyle = getCssVar('--color-fg-default') || getCssVar('--fgColor-default')
    ctx.font = "12px 'Helvetica Neue', Arial, sans-serif"
    ctx.fillText('Owners', 40, this.headerHeight / 2 + 3)
    const topBottomMargin = 10
    ctx.fillStyle = this.dividerColor
    ctx.fillRect(this.namesWidth - 1, topBottomMargin, 1, this.headerHeight - topBottomMargin * 2)
    ctx.fillStyle = this.dividerColor
    ctx.fillRect(0, this.headerHeight - 1, this.namesWidth, 1)
    ctx.fillStyle = this.dividerColor
    ctx.fillRect(this.namesWidth - 1, this.headerHeight, 1, this.network.height - this.headerHeight)
  }
}

type HeadsMap = {[key: string]: Array<{name: string; head: Head}>}

class NetworkGraph {
  network: Network
  ctx: CanvasRenderingContext2D
  commits: Commit[]
  users: UserMap
  spaceMap: SpaceMap
  userBlocks: UserBlock[]
  nethash: string
  namesWidth: number
  headerHeight: number
  dateRowHeight: number
  graphTopOffset: number
  bgColors: string[]
  nameLineHeight: number
  spaceColors: string[]
  offsetX: number
  offsetY: number
  bgCycle: number
  marginMap: {[key: number]: Array<[number, number]>}
  gravatars: {[key: string]: HTMLImageElement}
  activeCommit: Commit | null
  contentHeight: number
  graphMidpoint: number
  showRefs: boolean
  lastHotLoadCenterIndex: number | null
  connectionMap: {[key: number]: Commit[]}
  spaceUserMap: {[key: number]: User}
  headsMap: HeadsMap

  constructor(
    network: Network,
    ctx1: CanvasRenderingContext2D,
    commits1: Commit[],
    users: UserMap,
    spaceMap: SpaceMap,
    userBlocks: UserBlock[],
    nethash: string,
  ) {
    let block
    let i
    let ix
    let l
    let len
    let len1
    let len2
    let m
    let n
    let ref2
    let ref3
    this.network = network
    this.ctx = ctx1
    this.commits = commits1
    this.users = users
    this.spaceMap = spaceMap
    this.userBlocks = userBlocks
    this.nethash = nethash
    this.namesWidth = 120
    this.headerHeight = 40
    this.dateRowHeight = 30
    this.graphTopOffset = 10 + this.headerHeight + this.dateRowHeight
    this.bgColors = [
      getCssVar('--color-canvas-default') || getCssVar('--bgColor-default'),
      getCssVar('--color-canvas-subtle') || getCssVar('--bgColor-muted'),
    ]
    this.nameLineHeight = 24
    this.spaceColors = [
      '#c0392b',
      '#3498db',
      '#2ecc71',
      '#8e44ad',
      '#f1c40f',
      '#e67e22',
      '#34495e',
      '#e74c3c',
      '#2980b9',
      '#1abc9c',
      '#9b59b6',
      '#f39c12',
      '#7f8c8d',
      '#2c3e50',
      '#d35400',
      '#e74c3c',
      '#95a5a6',
      '#bdc3c7',
      '#16a085',
      '#27ae60',
    ]
    this.offsetX = this.namesWidth + (network.width - this.namesWidth) / 2 - network.focus! * this.nameLineHeight
    this.offsetY = 0
    this.bgCycle = 0
    this.marginMap = {}
    this.gravatars = {}
    this.activeCommit = null
    this.contentHeight = this.calcContentHeight()
    this.graphMidpoint = this.namesWidth + (network.width - this.namesWidth) / 2
    this.showRefs = true
    this.lastHotLoadCenterIndex = null
    this.connectionMap = {}
    this.spaceUserMap = {}
    const ref1 = this.userBlocks
    for (i = 0, len = ref1.length; i < len; i++) {
      block = ref1[i]!
      for (
        ix = l = ref2 = block.start, ref3 = block.start + block.count;
        ref2 <= ref3 ? l < ref3 : l > ref3;
        ix = ref2 <= ref3 ? ++l : --l
      ) {
        this.spaceUserMap[ix] = this.users[block.name]!
      }
    }
    this.headsMap = {} as HeadsMap
    const ref4 = this.userBlocks
    for (m = 0, len1 = ref4.length; m < len1; m++) {
      block = ref4[m]!
      const user = this.users[block.name]!
      const ref5 = user.heads
      for (n = 0, len2 = ref5.length; n < len2; n++) {
        const head = ref5[n]!
        if (!this.headsMap[head.id]) {
          this.headsMap[head.id] = []
        }

        const headPair = {
          name: user.name,
          head,
        }

        this.headsMap[head.id]!.push(headPair)
      }
    }
  }

  moveX(deltaX: number) {
    this.offsetX += deltaX
    if (this.offsetX > this.graphMidpoint) {
      this.offsetX = this.graphMidpoint
    } else if (this.offsetX < this.graphMidpoint - this.commits.length * this.nameLineHeight) {
      this.offsetX = this.graphMidpoint - this.commits.length * this.nameLineHeight
    }
    this.hotLoadCommits()
  }

  moveY(deltaY: number) {
    this.offsetY += deltaY
    if (this.offsetY > 0 || this.contentHeight < this.network.height - this.graphTopOffset) {
      this.offsetY = 0
    } else if (this.offsetY < -this.contentHeight + this.network.height / 2) {
      this.offsetY = -this.contentHeight + this.network.height / 2
    }
  }

  toggleRefs() {
    this.showRefs = !this.showRefs
  }

  calcContentHeight() {
    let i
    let len
    let height = 0
    const ref1 = this.userBlocks
    for (i = 0, len = ref1.length; i < len; i++) {
      const block = ref1[i]!
      height += block.count
    }
    return height * this.nameLineHeight
  }

  hover(x: number, y: number) {
    let i
    let ix
    let ref1
    let ref2
    const window = this.timeWindow()
    for (
      ix = i = ref1 = window.min, ref2 = window.max;
      ref1 <= ref2 ? i <= ref2 : i >= ref2;
      ix = ref1 <= ref2 ? ++i : --i
    ) {
      const commit = this.commits[ix]!
      const tx = this.offsetX + commit.time * this.nameLineHeight
      const ty = this.offsetY + this.graphTopOffset + commit.space * this.nameLineHeight
      if (x > tx - 5 && x < tx + 5 && y > ty - 5 && y < ty + 5) {
        return commit
      }
    }
    return null
  }

  hotLoadCommits() {
    let centerIndex
    const maxSpan = 200
    centerIndex = Math.floor((-this.offsetX + this.graphMidpoint) / this.nameLineHeight)
    if (centerIndex < 0) {
      centerIndex = 0
    }
    if (centerIndex > this.commits.length - 1) {
      centerIndex = this.commits.length - 1
    }
    if (this.lastHotLoadCenterIndex != null && Math.abs(this.lastHotLoadCenterIndex - centerIndex) < 10) {
      return
    } else {
      this.lastHotLoadCenterIndex = centerIndex
    }
    const backSpan = this.backSpan(centerIndex, maxSpan)
    const frontSpan = this.frontSpan(centerIndex, maxSpan)
    if (backSpan && frontSpan) {
      return this.requestChunk(backSpan[0], frontSpan[1])
    } else if (frontSpan) {
      return this.requestChunk(frontSpan[0], frontSpan[1])
    } else if (backSpan) {
      return this.requestChunk(backSpan[0], backSpan[1])
    }
  }

  backSpan(centerIndex: number, maxSpan: number): [number, number] | null {
    let i
    let ix
    let l
    let leftEmptyCommitIndex
    let leftPresentCommitIndex
    let leftmostEmptyCommitIndex
    let ref1
    let ref2
    leftEmptyCommitIndex = null
    for (ix = i = ref1 = centerIndex; ref1 <= 0 ? i <= 0 : i >= 0; ix = ref1 <= 0 ? ++i : --i) {
      if (!(ix > centerIndex - maxSpan)) {
        break
      }
      if (!this.commits[ix]!.requested) {
        leftEmptyCommitIndex = ix
        break
      }
    }
    if (leftEmptyCommitIndex !== null) {
      leftPresentCommitIndex = null
      leftmostEmptyCommitIndex = null
      for (ix = l = ref2 = leftEmptyCommitIndex; ref2 <= 0 ? l <= 0 : l >= 0; ix = ref2 <= 0 ? ++l : --l) {
        if (!(ix > leftEmptyCommitIndex - maxSpan)) {
          break
        }
        if (this.commits[ix]!.requested) {
          leftPresentCommitIndex = ix
          break
        }
      }
      if (leftPresentCommitIndex != null) {
        leftmostEmptyCommitIndex = leftPresentCommitIndex + 1
      } else {
        leftmostEmptyCommitIndex = leftEmptyCommitIndex - maxSpan
        if (leftmostEmptyCommitIndex < 0) {
          leftmostEmptyCommitIndex = 0
        }
      }
      return [leftmostEmptyCommitIndex, leftEmptyCommitIndex]
    } else {
      return null
    }
  }

  frontSpan(centerIndex: number, maxSpan: number): [number, number] | null {
    let i
    let ix
    let l
    let ref1
    let ref2
    let ref3
    let ref4
    let rightEmptyCommitIndex
    let rightPresentCommitIndex
    let rightmostEmptyCommitIndex
    rightEmptyCommitIndex = null
    for (
      ix = i = ref1 = centerIndex, ref2 = this.commits.length;
      ref1 <= ref2 ? i < ref2 : i > ref2;
      ix = ref1 <= ref2 ? ++i : --i
    ) {
      if (!(ix < centerIndex + maxSpan)) {
        break
      }
      if (!this.commits[ix]!.requested) {
        rightEmptyCommitIndex = ix
        break
      }
    }
    if (rightEmptyCommitIndex !== null) {
      rightPresentCommitIndex = null
      rightmostEmptyCommitIndex = null
      for (
        ix = l = ref3 = rightEmptyCommitIndex, ref4 = this.commits.length;
        ref3 <= ref4 ? l < ref4 : l > ref4;
        ix = ref3 <= ref4 ? ++l : --l
      ) {
        if (!(ix < rightEmptyCommitIndex + maxSpan)) {
          break
        }
        if (this.commits[ix]!.requested) {
          rightPresentCommitIndex = ix
          break
        }
      }
      if (rightPresentCommitIndex != null) {
        rightmostEmptyCommitIndex = rightPresentCommitIndex - 1
      } else if (rightEmptyCommitIndex + maxSpan >= this.commits.length) {
        rightmostEmptyCommitIndex = this.commits.length - 1
      } else {
        rightmostEmptyCommitIndex = rightEmptyCommitIndex + maxSpan
      }
      return [rightEmptyCommitIndex, rightmostEmptyCommitIndex]
    } else {
      return null
    }
  }

  chunkUrl(params: {[key: string]: string}) {
    const container = document.querySelector<HTMLElement>('.js-network-graph-container')!
    const baseUrl = container.getAttribute('data-network-graph-chunk-url')!
    const url = new URL(baseUrl, window.location.origin)

    const search = new URLSearchParams(url.search.slice(1))
    for (const key in params) {
      search.append(key, params[key]!)
    }
    url.search = search.toString()

    return url.toString()
  }

  async requestInitialChunk() {
    if (!viewingNetwork) return

    const url = this.chunkUrl({nethash: this.nethash})
    const response = await fetch(url, {
      headers: {
        ...getBaseFetchHeaders(),
        Accept: 'application/json',
      },
    })
    if (!response.ok) {
      const responseError = new Error()
      const statusText = response.statusText ? ` ${response.statusText}` : ''
      responseError.message = `HTTP ${response.status}${statusText}`
      throw responseError
    }
    const chunk = await response.json()
    this.importChunk(chunk)
    this.draw()
    this.network.chrome!.draw()
  }

  async requestChunk(start: number, end: number): Promise<void> {
    let i
    let ix
    let ref1
    let ref2
    if (!viewingNetwork) {
      return
    }
    for (ix = i = ref1 = start, ref2 = end; ref1 <= ref2 ? i <= ref2 : i >= ref2; ix = ref1 <= ref2 ? ++i : --i) {
      this.commits[ix]!.requested = true
    }

    const url = this.chunkUrl({nethash: this.nethash, start: start.toString(), end: end.toString()})
    const response = await fetch(url, {
      headers: {
        ...getBaseFetchHeaders(),
        Accept: 'application/json',
      },
    })
    if (!response.ok) {
      const responseError = new Error()
      const statusText = response.statusText ? ` ${response.statusText}` : ''
      responseError.message = `HTTP ${response.status}${statusText}`
      throw responseError
    }
    const chunk = await response.json()
    this.importChunk(chunk)
    this.draw()
    this.network.chrome!.draw()
    this.lastHotLoadCenterIndex = this.network.focus
  }

  importChunk(chunk: Chunk) {
    let i
    let k
    let len
    if (!chunk.commits) {
      return
    }
    const ref1 = chunk.commits
    const results = []
    for (i = 0, len = ref1.length; i < len; i++) {
      const ccommit = ref1[i]!
      const user = this.spaceUserMap[ccommit.space]!
      const commit = this.commits[ccommit.time]!
      commit.populate(ccommit, user, this.commits)
      let l
      let len1
      const ref2 = commit.parents
      const results1 = []
      for (l = 0, len1 = ref2.length; l < len1; l++) {
        const pcommit = ref2[l]!
        let m
        let ref3
        let ref4
        const results2 = []
        for (
          k = m = ref3 = pcommit.time + 1, ref4 = commit.time;
          ref3 <= ref4 ? m < ref4 : m > ref4;
          k = ref3 <= ref4 ? ++m : --m
        ) {
          this.connectionMap[k] = this.connectionMap[k] || []
          results2.push(this.connectionMap[k]!.push(commit))
        }
        results1.push(results2)
      }
      results.push(results1)
    }
    return results
  }

  timeWindow() {
    let maxCommitIndex
    let minCommitIndex
    minCommitIndex = Math.floor((this.namesWidth - this.offsetX + this.nameLineHeight) / this.nameLineHeight)
    if (minCommitIndex < 0) {
      minCommitIndex = 0
    }
    maxCommitIndex = minCommitIndex + Math.floor((this.network.width - this.namesWidth) / this.nameLineHeight)
    if (maxCommitIndex > this.commits.length - 1) {
      maxCommitIndex = this.commits.length - 1
    }
    return {
      min: minCommitIndex,
      max: maxCommitIndex,
    }
  }

  draw() {
    let commit
    let i
    let ix
    let j
    let l
    let len
    let len1
    let len2
    let len3
    let m
    let n
    let o
    let q
    let r
    let ref10
    let ref2
    let ref3
    let ref4
    let ref5
    let ref7
    let ref8
    let ref9
    let rspace
    let s
    this.drawBackground()
    const timeWindow = this.timeWindow()
    const minCommitIndex = timeWindow.min
    const maxCommitIndex = timeWindow.max
    this.ctx.save()
    this.ctx.translate(this.offsetX, this.offsetY + this.graphTopOffset)
    const drawMap: {[key: string]: boolean} = {}
    const ref1 = this.spaceMap
    for (ix = i = 0, len = ref1.length; i < len; ix = ++i) {
      rspace = this.spaceMap.length - ix - 1
      for (
        j = l = ref2 = minCommitIndex, ref3 = maxCommitIndex;
        ref2 <= ref3 ? l <= ref3 : l >= ref3;
        j = ref2 <= ref3 ? ++l : --l
      ) {
        commit = this.commits[j]!
        if (commit.populated && commit.space === rspace) {
          this.drawConnection(commit)
          drawMap[commit.id!] = true
        }
      }
    }
    for (
      ix = m = ref4 = minCommitIndex, ref5 = maxCommitIndex;
      ref4 <= ref5 ? m <= ref5 : m >= ref5;
      ix = ref4 <= ref5 ? ++m : --m
    ) {
      const commits = this.connectionMap[ix]
      if (!commits) {
        continue
      }
      for (n = 0, len1 = commits.length; n < len1; n++) {
        commit = commits[n]!
        if (!drawMap[commit.id!]) {
          this.drawConnection(commit)
          drawMap[commit.id!] = true
        }
      }
    }
    const ref6 = this.spaceMap
    for (ix = o = 0, len2 = ref6.length; o < len2; ix = ++o) {
      rspace = this.spaceMap.length - ix - 1
      for (
        j = q = ref7 = minCommitIndex, ref8 = maxCommitIndex;
        ref7 <= ref8 ? q <= ref8 : q >= ref8;
        j = ref7 <= ref8 ? ++q : --q
      ) {
        commit = this.commits[j]!
        if (commit.populated && commit.space === rspace) {
          if (commit === this.activeCommit) {
            this.drawActiveCommit(commit)
          } else {
            this.drawCommit(commit)
          }
        }
      }
    }
    if (this.showRefs) {
      for (
        j = r = ref9 = minCommitIndex, ref10 = maxCommitIndex;
        ref9 <= ref10 ? r <= ref10 : r >= ref10;
        j = ref9 <= ref10 ? ++r : --r
      ) {
        commit = this.commits[j]!
        if (!commit.populated) {
          continue
        }
        const headPairs = this.headsMap[commit.id!]
        if (!headPairs) {
          continue
        }
        let currentOffset = 0
        for (s = 0, len3 = headPairs.length; s < len3; s++) {
          const headPair = headPairs[s]!
          if (this.spaceUserMap[commit.space]!.name === headPair.name) {
            const newOff = this.drawHead(commit, headPair.head, currentOffset)
            currentOffset += newOff
          }
        }
      }
    }
    this.ctx.restore()
    if (this.activeCommit) {
      return this.drawCommitInfo(this.activeCommit)
    }
  }

  drawBackground() {
    let i
    let ix
    let len
    this.ctx.clearRect(0, 0, this.network.width, this.network.height)
    this.ctx.save()
    this.ctx.translate(0, this.offsetY + this.graphTopOffset)
    this.ctx.clearRect(0, -10, this.network.width, this.network.height)
    const ref1 = this.userBlocks
    for (ix = i = 0, len = ref1.length; i < len; ix = ++i) {
      const block = ref1[ix]!
      this.ctx.fillStyle = this.bgColors[ix % 2]!
      this.ctx.fillRect(
        0,
        block.start * this.nameLineHeight - 10,
        this.network.width,
        block.count * this.nameLineHeight,
      )
      this.ctx.fillStyle = getCssVar('--color-border-default') || getCssVar('--borderColor-default')
      this.ctx.fillRect(0, (block.start + block.count) * this.nameLineHeight - 11, this.network.width, 1)
    }
    return this.ctx.restore()
  }

  drawCommit(commit: Commit) {
    const x = commit.time * this.nameLineHeight
    const y = commit.space * this.nameLineHeight
    this.ctx.beginPath()
    this.ctx.arc(x, y, 3, 0, 2 * Math.PI, false)
    this.ctx.fillStyle = this.spaceColor(commit.space)!
    return this.ctx.fill()
  }

  drawActiveCommit(commit: Commit) {
    const x = commit.time * this.nameLineHeight
    const y = commit.space * this.nameLineHeight
    this.ctx.beginPath()
    this.ctx.arc(x, y, 6, 0, 2 * Math.PI, false)
    this.ctx.fillStyle = this.spaceColor(commit.space)!
    return this.ctx.fill()
  }

  drawCommitInfo(commit: Commit) {
    const borderRadius = 3
    const boxWidth = 340
    const minHeight = 56
    const messageLines = commit.message ? this.splitLines(commit.message, 48) : ([] as string[])
    const infoHeight = Math.max(minHeight, 38 + 16 * messageLines.length)
    const commitX = this.offsetX + commit.time * this.nameLineHeight
    const commitY = this.graphTopOffset + this.offsetY + commit.space * this.nameLineHeight
    let infoX = 0
    let infoY = 0
    if (commitX < this.graphMidpoint) {
      infoX = commitX + 10
    } else {
      infoX = commitX - (boxWidth + 10)
    }
    if (commitY < 40 + (this.network.height - 40) / 2) {
      infoY = commitY + 10
    } else {
      infoY = commitY - infoHeight - 10
    }
    this.ctx.save()
    this.ctx.translate(infoX, infoY)
    this.ctx.fillStyle = getCssVar('--color-canvas-overlay') || getCssVar('--overlay-bgColor')
    this.ctx.strokeStyle = getCssVar('--color-border-default') || getCssVar('--borderColor-default')
    this.ctx.lineWidth = 1
    this.roundRect(0, 0, boxWidth, infoHeight, borderRadius)
    let gravatar = this.gravatars[commit.gravatar!]
    if (gravatar) {
      this.drawGravatar(gravatar, 10, 10)
    } else {
      gravatar = new Image()
      gravatar.src = commit.gravatar!
      gravatar.onload = (function (_this) {
        return function () {
          if (_this.activeCommit === commit) {
            _this.drawGravatar(gravatar!, infoX + 10, infoY + 10)
            _this.gravatars[commit.gravatar!] = gravatar!
          }
        }
      })(this)
    }
    this.ctx.fillStyle = getCssVar('--color-fg-default') || getCssVar('--fgColor-default')
    this.ctx.font = "bold 12px 'Helvetica Neue', Arial, sans-serif"
    this.ctx.fillText(commit.author!, 55, 24)
    this.ctx.fillStyle = getCssVar('--color-fg-muted') || getCssVar('--fgColor-muted')
    this.ctx.font = '11px Consolas, Menlo, Courier, monospace'
    if (commit.id) {
      this.ctx.fillText(commit.id.slice(0, 7), 280, 24)
    }
    this.drawMessage(messageLines, 55, 41)
    return this.ctx.restore()
  }

  drawGravatar(image: HTMLImageElement, x: number, y: number) {
    const size = 32
    this.ctx.save()
    this.ctx.fillStyle = '#fff'
    this.ctx.strokeStyle = 'rgba(0, 0, 0, 0.0)'
    this.ctx.lineWidth = 0.1
    this.roundRect(x + 2, y + 2, size, size, 4)
    this.ctx.clip()
    this.ctx.drawImage(image, x + 2, y + 2, size, size)
    return this.ctx.restore()
  }

  roundRect(x: number, y: number, width: number, height: number, radius: number) {
    this.ctx.beginPath()
    this.ctx.moveTo(x, y + radius)
    this.ctx.lineTo(x, y + height - radius)
    this.ctx.quadraticCurveTo(x, y + height, x + radius, y + height)
    this.ctx.lineTo(x + width - radius, y + height)
    this.ctx.quadraticCurveTo(x + width, y + height, x + width, y + height - radius)
    this.ctx.lineTo(x + width, y + radius)
    this.ctx.quadraticCurveTo(x + width, y, x + width - radius, y)
    this.ctx.lineTo(x + radius, y)
    this.ctx.quadraticCurveTo(x, y, x, y + radius)
    this.ctx.fill()
    return this.ctx.stroke()
  }

  drawMessage(lines: string[], x: number, y: number) {
    let i
    let ix
    let len
    this.ctx.font = "12px 'Helvetica Neue', Arial, sans-serif"
    this.ctx.fillStyle = getCssVar('--color-fg-default') || getCssVar('--fgColor-default')
    const results = []
    for (ix = i = 0, len = lines.length; i < len; ix = ++i) {
      const line = lines[ix]!
      results.push(this.ctx.fillText(line, x, y + ix * 16))
    }
    return results
  }

  splitLines(text: string, max: number) {
    let i
    let len
    let line
    const words = text.split(' ')
    const lines = []
    line = ''
    for (i = 0, len = words.length; i < len; i++) {
      const word = words[i]!
      if (line.length + 1 + word.length < max) {
        line = line === '' ? word : `${line} ${word}`
      } else {
        lines.push(line)
        line = word
      }
    }
    lines.push(line)
    return lines
  }

  drawHead(commit: Commit, head: Head, headOffset: number) {
    this.ctx.font = "11px 'Helvetica Neue', Arial, sans-serif"
    this.ctx.save()
    const headWidth = this.ctx.measureText(head.name).width
    this.ctx.restore()
    const x = commit.time * this.nameLineHeight
    const y = commit.space * this.nameLineHeight + 5 + headOffset
    const verticalOffset = 2.5
    this.ctx.save()
    this.ctx.translate(x, y - verticalOffset)
    this.ctx.fillStyle = getCssVar('--color-neutral-emphasis-plus') || getCssVar('--bgColor-emphasis')
    this.ctx.beginPath()
    this.ctx.moveTo(0, verticalOffset)
    this.ctx.lineTo(-4, 10)
    this.ctx.quadraticCurveTo(-9, 10, -9, 15)
    this.ctx.lineTo(-9, 15 + headWidth)
    this.ctx.quadraticCurveTo(-9, 15 + headWidth + 5, -4, 15 + headWidth + 5)
    this.ctx.lineTo(4, 15 + headWidth + 5)
    this.ctx.quadraticCurveTo(9, 15 + headWidth + 5, 9, 15 + headWidth)
    this.ctx.lineTo(9, 15)
    this.ctx.quadraticCurveTo(9, 10, 4, 10)
    this.ctx.lineTo(0, verticalOffset)
    this.ctx.fill()
    this.ctx.fillStyle = getCssVar('--color-fg-on-emphasis') || getCssVar('--fgColor-onEmphasis')
    this.ctx.font = "12px 'Helvetica Neue', Arial, sans-serif"
    this.ctx.textBaseline = 'middle'
    this.ctx.scale(0.85, 0.85)
    this.ctx.rotate(Math.PI / 2)
    this.ctx.fillText(head.name, 19, -0.5)
    this.ctx.restore()
    return headWidth + this.nameLineHeight
  }

  drawConnection(c: Commit) {
    let i
    let ix
    let len
    const ref1 = c.parents
    const results = []
    for (ix = i = 0, len = ref1.length; i < len; ix = ++i) {
      const p = ref1[ix]!
      if (ix === 0) {
        if (p.space === c.space) {
          results.push(this.drawBasicConnection(p, c))
        } else {
          results.push(this.drawBranchConnection(p, c))
        }
      } else {
        results.push(this.drawMergeConnection(p, c))
      }
    }
    return results
  }

  drawBasicConnection(p: Commit, c: Commit) {
    const color = this.spaceColor(c.space)
    this.ctx.strokeStyle = color
    this.ctx.lineWidth = 2
    this.ctx.beginPath()
    this.ctx.moveTo(p.time * this.nameLineHeight, c.space * this.nameLineHeight)
    this.ctx.lineTo(c.time * this.nameLineHeight, c.space * this.nameLineHeight)
    return this.ctx.stroke()
  }

  drawBranchConnection(p: Commit, c: Commit) {
    const color = this.spaceColor(c.space)
    this.ctx.strokeStyle = color
    this.ctx.lineWidth = 2
    this.ctx.beginPath()
    this.ctx.moveTo(p.time * this.nameLineHeight, p.space * this.nameLineHeight)
    this.ctx.lineTo(p.time * this.nameLineHeight, c.space * this.nameLineHeight)
    this.ctx.lineTo(c.time * this.nameLineHeight - 10, c.space * this.nameLineHeight)
    this.ctx.stroke()
    return this.threeClockArrow(color, c.time * this.nameLineHeight, c.space * this.nameLineHeight)
  }

  drawMergeConnection(p: Commit, c: Commit) {
    let margin
    const color = this.spaceColor(p.space)
    this.ctx.strokeStyle = color
    this.ctx.lineWidth = 2
    this.ctx.beginPath()
    if (p.space > c.space) {
      this.ctx.moveTo(p.time * this.nameLineHeight, p.space * this.nameLineHeight)
      const safe = this.safePath(p.time, c.time, p.space)
      if (safe) {
        this.ctx.lineTo(c.time * this.nameLineHeight - 10, p.space * this.nameLineHeight)
        this.ctx.lineTo(c.time * this.nameLineHeight - 10, c.space * this.nameLineHeight + 15)
        this.ctx.lineTo(c.time * this.nameLineHeight - 5.7, c.space * this.nameLineHeight + 7.5)
        this.ctx.stroke()
        return this.oneClockArrow(color, c.time * this.nameLineHeight, c.space * this.nameLineHeight)
      }
      margin = this.closestMargin(p.time, c.time, p.space, -1)
      if (p.space === c.space + 1 && p.space === margin + 1) {
        this.ctx.lineTo(p.time * this.nameLineHeight, margin * this.nameLineHeight + 10)
        this.ctx.lineTo(c.time * this.nameLineHeight - 15, margin * this.nameLineHeight + 10)
        this.ctx.lineTo(c.time * this.nameLineHeight - 9.5, margin * this.nameLineHeight + 7.7)
        this.ctx.stroke()
        this.twoClockArrow(color, c.time * this.nameLineHeight, margin * this.nameLineHeight)
        return this.addMargin(p.time, c.time, margin)
      } else if (p.time + 1 === c.time) {
        margin = this.closestMargin(p.time, c.time, c.space, 0)
        this.ctx.lineTo(p.time * this.nameLineHeight, margin * this.nameLineHeight + 10)
        this.ctx.lineTo(c.time * this.nameLineHeight - 15, margin * this.nameLineHeight + 10)
        this.ctx.lineTo(c.time * this.nameLineHeight - 15, c.space * this.nameLineHeight + 10)
        this.ctx.lineTo(c.time * this.nameLineHeight - 9.5, c.space * this.nameLineHeight + 7.7)
        this.ctx.stroke()
        this.twoClockArrow(color, c.time * this.nameLineHeight, c.space * this.nameLineHeight)
        return this.addMargin(p.time, c.time, margin)
      }
      this.ctx.lineTo(p.time * this.nameLineHeight + 10, p.space * this.nameLineHeight - 10)
      this.ctx.lineTo(p.time * this.nameLineHeight + 10, margin * this.nameLineHeight + 10)
      this.ctx.lineTo(c.time * this.nameLineHeight - 10, margin * this.nameLineHeight + 10)
      this.ctx.lineTo(c.time * this.nameLineHeight - 10, c.space * this.nameLineHeight + 15)
      this.ctx.lineTo(c.time * this.nameLineHeight - 5.7, c.space * this.nameLineHeight + 7.5)
      this.ctx.stroke()
      this.oneClockArrow(color, c.time * this.nameLineHeight, c.space * this.nameLineHeight)
      return this.addMargin(p.time, c.time, margin)
    }
    margin = this.closestMargin(p.time, c.time, c.space, -1)
    if (margin < c.space) {
      this.ctx.moveTo(p.time * this.nameLineHeight, p.space * this.nameLineHeight)
      this.ctx.lineTo(p.time * this.nameLineHeight, margin * this.nameLineHeight + 10)
      this.ctx.lineTo(c.time * this.nameLineHeight - 12.7, margin * this.nameLineHeight + 10)
      this.ctx.lineTo(c.time * this.nameLineHeight - 12.7, c.space * this.nameLineHeight - 10)
      this.ctx.lineTo(c.time * this.nameLineHeight - 9.4, c.space * this.nameLineHeight - 7.7)
      this.ctx.stroke()
      this.fourClockArrow(color, c.time * this.nameLineHeight, c.space * this.nameLineHeight)
      return this.addMargin(p.time, c.time, margin)
    }
    this.ctx.moveTo(p.time * this.nameLineHeight, p.space * this.nameLineHeight)
    this.ctx.lineTo(p.time * this.nameLineHeight, margin * this.nameLineHeight + 10)
    this.ctx.lineTo(c.time * this.nameLineHeight - 12.7, margin * this.nameLineHeight + 10)
    this.ctx.lineTo(c.time * this.nameLineHeight - 12.7, c.space * this.nameLineHeight + 10)
    this.ctx.lineTo(c.time * this.nameLineHeight - 9.4, c.space * this.nameLineHeight + 7.7)
    this.ctx.stroke()
    this.twoClockArrow(color, c.time * this.nameLineHeight, c.space * this.nameLineHeight)
    return this.addMargin(p.time, c.time, margin)
  }

  addMargin(a: number, b: number, space: number) {
    if (!this.marginMap[space]) {
      this.marginMap[space] = []
    }
    return this.marginMap[space].push([a, b])
  }

  oneClockArrow(color: string, x: number, y: number) {
    this.ctx.fillStyle = color
    this.ctx.beginPath()
    this.ctx.moveTo(x - 3.0, y + 10.5)
    this.ctx.lineTo(x - 9.0, y + 5.5)
    this.ctx.lineTo(x - 2.6, y + 3.5)
    return this.ctx.fill()
  }

  twoClockArrow(color: string, x: number, y: number) {
    this.ctx.fillStyle = color
    this.ctx.beginPath()
    this.ctx.moveTo(x - 12.4, y + 6.6)
    this.ctx.lineTo(x - 9.3, y + 10.6)
    this.ctx.lineTo(x - 3.2, y + 2.4)
    return this.ctx.fill()
  }

  threeClockArrow(color: string, x: number, y: number) {
    this.ctx.fillStyle = color
    this.ctx.beginPath()
    this.ctx.moveTo(x - 10, y - 3.5)
    this.ctx.lineTo(x - 10, y + 3.5)
    this.ctx.lineTo(x - 4, y)
    return this.ctx.fill()
  }

  fourClockArrow(color: string, x: number, y: number) {
    this.ctx.fillStyle = color
    this.ctx.beginPath()
    this.ctx.moveTo(x - 12.4, y - 6.6)
    this.ctx.lineTo(x - 9.3, y - 10.6)
    this.ctx.lineTo(x - 3.2, y - 2.4)
    return this.ctx.fill()
  }

  safePath(a: number, b: number, space: number): boolean {
    let i
    let len
    const ref1 = this.spaceMap[space]!
    for (i = 0, len = ref1.length; i < len; i++) {
      const path = ref1[i]!
      if (this.timeInPath(a, path)) {
        return path[1] === b
      }
    }
    return false
  }

  closestMargin(a: number, b: number, space: number, init: number): number {
    let above
    let below
    let distance
    let zerodone
    const max = this.spaceMap.length
    distance = init
    below = false
    above = false
    zerodone = false
    while (!(above && below)) {
      if (space + distance >= 0 && this.safeMargin(a, b, space + distance)) {
        return space + distance
      }
      if (space + distance < 0) {
        below = true
      }
      if (space + distance > max) {
        above = true
      }
      if (zerodone === false && distance === 0) {
        distance = -1
        zerodone = true
      } else if (distance < 0) {
        distance = -distance - 1
      } else {
        distance = -distance - 2
      }
    }
    if (space > 0) {
      return space - 1
    } else {
      return 0
    }
  }

  safeMargin(a: number, b: number, space: number) {
    let i
    let len
    if (!this.marginMap[space]) {
      return true
    }
    const ref1 = this.marginMap[space]
    for (i = 0, len = ref1.length; i < len; i++) {
      const path = ref1[i]!
      if (this.pathsCollide([a, b], path)) {
        return false
      }
    }
    return true
  }

  pathsCollide(a: number[], b: number[]) {
    return (
      this.timeWithinPath(a[0]!, b) ||
      this.timeWithinPath(a[1]!, b) ||
      this.timeWithinPath(b[0]!, a) ||
      this.timeWithinPath(b[1]!, a)
    )
  }

  timeInPath(time: number, path: number[]) {
    return time >= path[0]! && time <= path[1]!
  }

  timeWithinPath(time: number, path: number[]) {
    return time > path[0]! && time < path[1]!
  }

  spaceColor(space: number) {
    if (space === 0) {
      return getCssVar('--color-fg-default') || getCssVar('--fgColor-default')
    } else {
      return this.spaceColors[space % this.spaceColors.length]!
    }
  }
}

class MouseDriver {
  container: HTMLElement | null = null
  chrome: NetworkChrome
  graph: NetworkGraph
  dragging: boolean
  lastPoint: {[key: string]: number}
  lastHoverCommit: Commit | null
  lastHoverUser: User | null
  pressedCommit: Commit | null
  pressedUser: User | null
  canvas: HTMLCanvasElement
  canvasOffset: {top: number; left: number}

  constructor(container: HTMLElement, chrome: NetworkChrome, graph: NetworkGraph) {
    this.chrome = chrome
    this.graph = graph
    this.dragging = false
    this.lastPoint = {
      x: 0,
      y: 0,
    }
    this.lastHoverCommit = null
    this.lastHoverUser = null
    this.pressedCommit = null
    this.pressedUser = null
    this.canvas = container.getElementsByTagName('canvas')[0]!
    this.canvasOffset = offset(this.canvas)
    this.canvas.style.cursor = 'move'
    document.body.addEventListener('pointerup', () => this.up())
    document.body.addEventListener('pointermove', (event: PointerEvent) => this.docmove(event))
    this.canvas.addEventListener('pointerdown', () => this.down())
    this.canvas.addEventListener('pointermove', (event: PointerEvent) => this.move(event))
    this.canvas.addEventListener('pointerout', () => this.out())
  }

  up() {
    this.dragging = false
    if (this.pressedCommit && this.graph.activeCommit && this.graph.activeCommit === this.pressedCommit) {
      window.open(
        `/${this.graph.activeCommit.user!.name}/${this.graph.activeCommit.user!.repo}/commit/${this.graph.activeCommit
          .id!}`,
      )
    } else if (this.pressedUser && this.chrome.activeUser && this.chrome.activeUser === this.pressedUser) {
      window.location.href = `/${this.chrome.activeUser.name}/${this.chrome.activeUser.repo}/network`
    }
    this.pressedCommit = null
    this.pressedUser = null
  }

  down() {
    if (this.graph.activeCommit) {
      this.pressedCommit = this.graph.activeCommit
    } else if (this.chrome.activeUser) {
      this.pressedUser = this.chrome.activeUser
    } else {
      this.dragging = true
    }
  }

  docmove(e: PointerEvent) {
    const x = e.pageX
    const y = e.pageY
    if (this.dragging) {
      this.graph.moveX(x - this.lastPoint.x!)
      this.graph.moveY(y - this.lastPoint.y!)
      this.graph.draw()
      this.chrome.moveX(x - this.lastPoint.x!)
      this.chrome.moveY(y - this.lastPoint.y!)
      this.chrome.draw()
    }
    this.lastPoint.x = x
    this.lastPoint.y = y
  }

  move(e: PointerEvent) {
    const x = e.pageX
    const y = e.pageY
    if (this.dragging) {
      this.graph.moveX(x - this.lastPoint.x!)
      this.graph.moveY(y - this.lastPoint.y!)
      this.graph.draw()
      this.chrome.moveX(x - this.lastPoint.x!)
      this.chrome.moveY(y - this.lastPoint.y!)
      this.chrome.draw()
    } else {
      const hoverUser = this.chrome.hover(x - this.canvasOffset.left, y - this.canvasOffset.top)!
      if (hoverUser !== this.lastHoverUser) {
        this.canvas.style.cursor = hoverUser ? 'pointer' : 'move'
        this.chrome.activeUser = hoverUser
        this.chrome.draw()
        this.lastHoverUser = hoverUser
      } else {
        const hoverCommit = this.graph.hover(x - this.canvasOffset.left, y - this.canvasOffset.top)
        if (hoverCommit !== this.lastHoverCommit) {
          this.canvas.style.cursor = hoverCommit ? 'pointer' : 'move'
          this.graph.activeCommit = hoverCommit
          this.graph.draw()
          this.chrome.draw()
          this.lastHoverCommit = hoverCommit
        }
      }
    }
    this.lastPoint.x = x
    this.lastPoint.y = y
  }

  out() {
    this.graph.activeCommit = null
    this.chrome.activeUser = null
    this.graph.draw()
    this.chrome.draw()
    this.lastHoverCommit = null
    this.lastHoverUser = null
  }
}

class KeyDriver {
  chrome: NetworkChrome
  graph: NetworkGraph
  dirty: boolean

  constructor(chrome: NetworkChrome, graph: NetworkGraph) {
    this.chrome = chrome
    this.graph = graph
    this.dirty = false
    document.addEventListener('keydown', (event: KeyboardEvent) => this.down(event))
  }

  moveBothX(x: number) {
    this.graph.moveX(x)
    this.chrome.moveX(x)
    this.graph.activeCommit = null
    this.dirty = true
  }

  moveBothY(x: number) {
    this.graph.moveY(x)
    this.chrome.moveY(x)
    this.graph.activeCommit = null
    this.dirty = true
  }

  toggleRefs() {
    this.graph.toggleRefs()
    this.dirty = true
  }

  redraw() {
    if (this.dirty) {
      this.graph.draw()
      this.chrome.draw()
    }
    this.dirty = false
  }

  down(e: KeyboardEvent) {
    // TODO: Refactor to use data-hotkey
    /* eslint eslint-comments/no-use: off */

    if (e.target instanceof HTMLInputElement) {
      return true
    }
    if (!isShortcutAllowed(e)) return
    if (e.shiftKey) {
      switch (e.which) {
        case 37:
        case 72:
          this.moveBothX(999999)
          return this.redraw()
        case 38:
        case 75:
          this.moveBothY(999999)
          return this.redraw()
        case 39:
        case 76:
          this.moveBothX(-999999)
          return this.redraw()
        case 40:
        case 74:
          this.moveBothY(-999999)
          return this.redraw()
      }
    } else {
      const code = e.which
      if (code === 37 || code === 72) {
        this.moveBothX(100)
        return this.redraw()
      } else if (code === 38 || code === 75) {
        this.moveBothY(30)
        return this.redraw()
      } else if (code === 39 || code === 76) {
        this.moveBothX(-100)
        return this.redraw()
      } else if (code === 40 || code === 74) {
        this.moveBothY(-30)
        return this.redraw()
      } else if (code === 84) {
        this.toggleRefs()
        return this.redraw()
      }
    }
  }
}

let viewingNetwork = false

@controller
class NetworkGraphElement extends HTMLElement {
  @target declare graph: HTMLElement
  #eventAbortController!: AbortController | null

  connectedCallback() {
    const network = new Network(this)
    network.redraw()

    this.#eventAbortController?.abort()
    this.#eventAbortController = new AbortController()

    const {signal} = this.#eventAbortController

    viewingNetwork = true
    /* eslint-disable-next-line github/prefer-observers */
    window.addEventListener(
      'resize',
      throttle(() => network.redraw(), 100),
      {signal},
    )
  }

  disconnectedCallback() {
    viewingNetwork = false
    this.#eventAbortController?.abort()
  }
}
